'use client'

import { cn } from '@/lib/utils'
import { useStoreNavigation } from '@/store'
import { Typo3_834 } from '@/types'
import { Icon, LangSwitch } from '@/ui/components'
import * as NavigationMenuPrimitive from '@radix-ui/react-navigation-menu'
import Link from 'next/link'
import { usePathname } from 'next/navigation'
import { ComponentProps, memo, useEffect, useState } from 'react'
import { checkIsCurrentChild, getActiveItem } from './utils'

const navigationMenuTriggerStyle = cn(
  'group textstyle-lg-button group inline-grid grid-cols-[auto_1fr] gap-[4px] text-neutral-100 outline-none transition-[color,box-shadow]',
  'group global-focus',
  'ease-none transition-opacity duration-200 group-hover:opacity-60 hover:opacity-100'
)

function NavDesktop({
  viewport = true,
  data = [],
  ...props
}: ComponentProps<typeof NavigationMenuPrimitive.Root> & {
  viewport?: boolean
  data: Typo3_834['navigation']
}) {
  const { isNavOpen, setIsNavOpen } = useStoreNavigation()
  const pathname = usePathname()
  const [value, setValue] = useState('')
  const [dafaultValue, setDefaultValue] = useState('')

  const items = data
    ?.filter((item) => item.isHidden === 0)
    ?.map((item) => ({
      ...item,
      children: item?.children?.filter((child) => child.isHidden === 0)
    }))

  const changeValue = (value: string) => {
    if (value) {
      setValue(value)
    } else {
      if (isNavOpen) {
        setValue(dafaultValue)
      }
    }
  }

  useEffect(() => {
    const activeItem = getActiveItem(items, pathname)
    setTimeout(() => {
      setDefaultValue(`item-${activeItem?.uid?.toString()}`)
    }, 0)
  }, [items])

  useEffect(() => {
    setTimeout(() => {
      changeValue(dafaultValue)
    }, 0)
  }, [dafaultValue])

  return (
    <NavigationMenuPrimitive.Root
      data-slot="navigation-menu"
      data-viewport={viewport}
      value={value}
      onValueChange={(value) => changeValue(value)}
      className={cn(
        'group/navigation-menu grid md:grid-cols-[auto_1fr]',
        'grid-cols-1 max-md:relative'
      )}
      {...props}
    >
      <NavigationMenuPrimitive.List
        data-slot="navigation-menu-list"
        className={cn(
          'group flex flex-1 list-none flex-col items-start justify-center gap-1'
        )}
      >
        {items?.map((item) => (
          <NavigationMenuPrimitive.Item
            value={`item-${item.uid?.toString()}`}
            key={`${item.uid?.toString()}`}
            data-slot="navigation-menu-item"
            className={cn('relative h-full w-full')}
          >
            {item?.children && item?.children?.length > 0 ? (
              <>
                <NavigationMenuTrigger
                  data-trigger-value={`item-${item.uid?.toString()}`}
                  value={`item-${item.uid?.toString()}`}
                >
                  <NavigationMenuTriggerContent hasChildren={true}>
                    {item.title}
                  </NavigationMenuTriggerContent>
                </NavigationMenuTrigger>
                <NavigationMenuContent>
                  <ul
                    className={cn(
                      'bg-accent-300 p-padding-md pt-xl flex w-full flex-col text-neutral-100'
                    )}
                  >
                    <li className="text-accent-100">
                      <NavigationMenuLink
                        asChild
                        submenuLink={false}
                        active={item.link === pathname}
                      >
                        <Link
                          href={item.link as string}
                          className={cn(pathname === item.link && 'underline')}
                          onClick={() => setIsNavOpen(false)}
                        >
                          {item.title}
                        </Link>
                      </NavigationMenuLink>
                    </li>
                    <li>
                      <ul className={cn('gap-gutter md:columns-2')}>
                        {item?.children?.map((child) => (
                          <li
                            key={`subitem-${child.uid}`}
                            className="pt-gutter"
                          >
                            <NavigationMenuLink
                              asChild
                              active={checkIsCurrentChild(child.link, pathname)}
                            >
                              <Link
                                href={child.link as string}
                                className={cn(
                                  checkIsCurrentChild(child.link, pathname) &&
                                    'underline'
                                )}
                                onClick={() => setIsNavOpen(false)}
                              >
                                {child.title}
                              </Link>
                            </NavigationMenuLink>
                          </li>
                        ))}
                      </ul>
                    </li>
                  </ul>
                </NavigationMenuContent>
              </>
            ) : (
              <Link
                href={item.link as string}
                onClick={() => setIsNavOpen(false)}
                className={cn(navigationMenuTriggerStyle)}
              >
                <NavigationMenuTriggerContent hasChildren={false}>
                  {item.title}
                </NavigationMenuTriggerContent>
              </Link>
            )}
          </NavigationMenuPrimitive.Item>
        ))}
        <LangSwitch
          data-lang-switch-desktop
          className="mt-lg textstyle-md-button"
        />
      </NavigationMenuPrimitive.List>
      {viewport && <NavigationMenuViewport />}
    </NavigationMenuPrimitive.Root>
  )
}

function NavigationMenuTrigger({
  children,
  hasChildren = true,
  ...props
}: ComponentProps<typeof NavigationMenuPrimitive.Trigger> & {
  hasChildren?: boolean
}) {
  return (
    <NavigationMenuPrimitive.Trigger
      data-slot="navigation-menu-trigger"
      className={cn(navigationMenuTriggerStyle)}
      {...props}
    >
      {children}
    </NavigationMenuPrimitive.Trigger>
  )
}

const NavigationMenuTriggerContent = ({
  children,
  hasChildren
}: {
  children: React.ReactNode
  hasChildren?: boolean
}) => (
  <>
    <span
      className={cn(
        'bg-accent-300 relative flex h-full items-center justify-center'
      )}
    >
      <span className="flex aspect-square h-full w-full items-center justify-center opacity-100 transition-[transform_opacity] duration-200 ease-out group-data-[state=open]:rotate-90 group-data-[state=open]:opacity-0">
        {hasChildren ? (
          <Icon icon="material-symbols:add" width="1.25rem" />
        ) : (
          <Icon icon="material-symbols:chevron-right" width="1.25rem" />
        )}
      </span>
      <span className="absolute inset-0 flex items-center justify-center opacity-0 transition-opacity duration-200 group-data-[state=open]:opacity-100">
        <Icon
          icon="material-symbols:remove"
          width="1.25rem"
          className="group-data-[state=closed]:hidden"
        />
      </span>
    </span>
    <span className="bg-accent-300 px-lg py-sm block">{children}</span>
  </>
)

function NavigationMenuContent({
  className,
  ...props
}: ComponentProps<typeof NavigationMenuPrimitive.Content>) {
  return (
    <NavigationMenuPrimitive.Content
      data-slot="navigation-menu-content"
      className={cn(
        'data-[motion^=from-]:animate-in data-[motion^=to-]:animate-out data-[motion^=from-]:fade-in data-[motion^=to-]:fade-out data-[motion=from-end]:slide-in-from-right-10 data-[motion=from-start]:slide-in-from-left-10 data-[motion=to-end]:slide-out-to-right-10 data-[motion=to-start]:slide-out-to-left-10 duration-400',
        'pl-2xl absolute top-0 right-0 h-full w-full',
        className
      )}
      {...props}
    />
  )
}

function NavigationMenuViewport() {
  return (
    <div
      className={cn(
        'isolate z-50 grid grid-cols-1',
        'max-md:right-0 max-md:left-0'
      )}
      data-navigation-menu-viewport
    >
      <NavigationMenuPrimitive.Viewport
        data-slot="navigation-menu-viewport"
        className={cn(
          'origin-right-center data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-90 relative overflow-visible rounded-md'
          // 'border bg-red-200 shadow md:w-(--radix-navigation-menu-viewport-width)',
        )}
      />
    </div>
  )
}

function NavigationMenuLink({
  className,
  submenuLink = true,
  ...props
}: ComponentProps<typeof NavigationMenuPrimitive.Link> & {
  submenuLink?: boolean
}) {
  return (
    <NavigationMenuPrimitive.Link
      data-slot="navigation-menu-link"
      className={cn(
        submenuLink &&
          "textstyle-lg-button data-[active=true]:focus:bg-accent data-[active=true]:text-accent-foreground focus-visible:ring-ring/50 [&_svg:not([class*='text-'])]:text-muted-foreground",

        className
      )}
      {...props}
    />
  )
}

export default memo(NavDesktop)
